import http from '@utils/http';
import { useAuthStore } from '@stores/auth.store';
import { useMessage } from '@context/MessageProvider';
import { useNotification } from '@context/NotificationProvider';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export const useLoginMutation = () => {
    const queryClient = useQueryClient();
    const login = useAuthStore((state) => state.login);
    const { showMessage } = useMessage();

    return useMutation({
        mutationFn: async ({ email, password, portal }) => {
            return await login(email, password, portal);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['auth'] });
            showMessage('success', 'Login successful');
        },
        onError: (error) => {
            showMessage('error', error.message || 'Login failed', 6);
        }
    });
};

export const useRegisterMutation = () => {
    const queryClient = useQueryClient();
    const register = useAuthStore((state) => state.register);
    const { showMessage } = useMessage();
    const { showNotification } = useNotification();

    return useMutation({
        mutationFn: async (formData) => {
            return await register(formData);
        },
        onSuccess: (response) => {
            queryClient.invalidateQueries({ queryKey: ['auth'] });
            if (response?.routeToLogin) {
                showNotification('success', response.message || 'Registration successful', {
                    duration: 12000,
                    placement: 'bottomRight'
                });
            } else {
                showMessage('success', 'Registration successful', 6);
            }
        },
        onError: (error) => {
            showMessage('error', error.message || 'Registration failed', 6);
        }
    });
};

export const useSendOtpMutation = () => {
    const { showMessage } = useMessage();

    return useMutation({
        mutationFn: async (email) => {
            const response = await http.post('/auth/send-otp', { email });
            return response.data;
        },
        onSuccess: () => {
            showMessage('success', 'OTP sent successfully', 6);
        },
        onError: (error) => {
            showMessage('error', error.response?.data?.message || error.message || 'Failed to send OTP', 6);
        }
    });
};

export const useVerifyOtpMutation = () => {
    const { showMessage } = useMessage();

    return useMutation({
        mutationFn: async ({ email, otp }) => {
            const response = await http.post('/auth/verify-otp', { email, otp });
            return response.data;
        },
        onSuccess: () => {
            showMessage('success', 'OTP verified successfully', 6);
        },
        onError: (error) => {
            showMessage('error', error.response?.data?.message || error.message || 'OTP verification failed', 6);
        }
    });
};

export const useRequestPasswordResetMutation = () => {
    const { showMessage } = useMessage();

    return useMutation({
        mutationFn: async (email) => {
            const response = await http.post('/auth/forgot-password', { email });
            return response.data;
        },
        onSuccess: () => {
            showMessage('success', 'Password reset email sent', 6);
        },
        onError: (error) => {
            showMessage('error', error.response?.data?.message || error.message || 'Failed to send reset email', 6);
        }
    });
};

export const useResetPasswordMutation = () => {
    const { showMessage } = useMessage();

    return useMutation({
        mutationFn: async ({ token, password, confirmPassword }) => {
            const response = await http.post('/auth/reset-password', {
                token,
                password,
                confirmPassword
            });
            return response.data;
        },
        onSuccess: () => {
            showMessage('success', 'Password reset successfully', 6);
        },
        onError: (error) => {
            showMessage('error', error.response?.data?.message || error.message || 'Password reset failed', 6);
        }
    });
};